
import { User, LogOut, LayoutDashboard, UserRound, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";

interface UserMenuProps {
  onLogin: () => void;
  onSave?: () => Promise<void>;
  hasUnsavedChanges?: boolean;
}

const UserMenu = ({ onLogin, onSave, hasUnsavedChanges }: UserMenuProps) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const handleNavigation = (path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowUnsavedDialog(true);
    } else {
      navigate(path);
    }
  };

  const handleSignOut = async () => {
    if (hasUnsavedChanges) {
      setPendingNavigation('signout');
      setShowUnsavedDialog(true);
      return;
    }

    try {
      await signOut();
      toast({
        title: "Uitgelogd",
        description: "Je bent succesvol uitgelogd.",
      });
    } catch (error) {
      toast({
        title: "Fout bij uitloggen",
        description: "Er ging iets mis bij het uitloggen.",
        variant: "destructive",
      });
    }
  };

  const handleSaveAndContinue = async () => {
    try {
      if (onSave) {
        await onSave();
      }
      
      if (pendingNavigation === 'signout') {
        await signOut();
        toast({
          title: "Uitgelogd",
          description: "Je bent succesvol uitgelogd.",
        });
      } else if (pendingNavigation) {
        navigate(pendingNavigation);
      }
    } catch (error) {
      toast({
        title: "Fout bij opslaan",
        description: "Er ging iets mis bij het opslaan van je werk.",
        variant: "destructive",
      });
    } finally {
      setShowUnsavedDialog(false);
      setPendingNavigation(null);
    }
  };

  const handleDiscardAndContinue = async () => {
    try {
      if (pendingNavigation === 'signout') {
        await signOut();
        toast({
          title: "Uitgelogd",
          description: "Je bent succesvol uitgelogd.",
        });
      } else if (pendingNavigation) {
        navigate(pendingNavigation);
      }
    } catch (error) {
      toast({
        title: "Fout",
        description: "Er ging iets mis.",
        variant: "destructive",
      });
    } finally {
      setShowUnsavedDialog(false);
      setPendingNavigation(null);
    }
  };

  if (!user) {
    return (
      <Button variant="ghost" size="sm" onClick={onLogin}>
        Inloggen
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 bg-[#E8F5E9] text-gray-900 hover:bg-[#D7ECD8]"
                >
                  <UserRound className="h-5 w-5" />
                  <span className="sr-only">Gebruikersmenu</span>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Profiel</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent align="end">
          <DropdownMenuItem className="text-sm text-muted-foreground">
            {user.email}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleNavigation('/instellingen')} className="gap-2">
            <Settings className="h-4 w-4" />
            Instellingen
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSignOut} className="gap-2">
            <LogOut className="h-4 w-4" />
            Uitloggen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onOpenChange={setShowUnsavedDialog}
        onSave={handleSaveAndContinue}
        onDiscard={handleDiscardAndContinue}
      />
    </>
  );
};

export default UserMenu;
